// Shared Crayo components: Logo, Icons, Header, Footer, Toast, CookieBanner
import React from 'react';
import { setConsent, shouldShowBanner, subscribe } from '../src/consent.js';

const { useState, useEffect, useRef, useCallback } = React;

// --- Hand-drawn SVG doodles (1.5px stroke, no fill) ---
const Icon = {
  LogoO: ({ size = 18, stroke = 2 }) => (
    <svg className="logo-o" viewBox="0 0 32 32" width={size} height={size} fill="none" aria-hidden="true">
      <path d="M16 3.5 C 23 3 28.5 8 28.5 16 C 28.5 24.5 22.5 28.2 16 28.5 C 9 28.8 3.5 24 3.5 16 C 3.5 8.5 8.5 4 16 3.5 Z"
            stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  Phone: () => (
    <svg viewBox="0 0 64 64" width="64" height="64" fill="none" aria-hidden="true">
      <rect x="18" y="6" width="28" height="52" rx="4.5" stroke="#1A1A1A" strokeWidth="1.5" />
      <line x1="27" y1="52" x2="37" y2="52" stroke="#1A1A1A" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M32 28 C 28 24 24 26 24 30 C 24 34 32 38 32 38 C 32 38 40 34 40 30 C 40 26 36 24 32 28 Z"
        stroke="#C65D3B" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  ),
  Pencil: () => (
    <svg viewBox="0 0 64 64" width="64" height="64" fill="none" aria-hidden="true">
      <path d="M10 48 L 40 18 L 50 28 L 20 58 Z" stroke="#1A1A1A" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M40 18 L 46 12 L 56 22 L 50 28" stroke="#1A1A1A" strokeWidth="1.5" strokeLinejoin="round" />
      <line x1="14" y1="52" x2="22" y2="60" stroke="#1A1A1A" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M10 48 L 6 62 L 20 58" stroke="#1A1A1A" strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  ),
  Crayons: () => (
    <svg viewBox="0 0 64 64" width="64" height="64" fill="none" aria-hidden="true">
      <path d="M14 14 L 20 8 L 26 14 L 20 58 L 14 58 Z" stroke="#1A1A1A" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M28 14 L 34 8 L 40 14 L 34 58 L 28 58 Z" stroke="#C65D3B" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M42 14 L 48 8 L 54 14 L 48 58 L 42 58 Z" stroke="#1A1A1A" strokeWidth="1.5" strokeLinejoin="round" />
      <line x1="16" y1="20" x2="24" y2="20" stroke="#1A1A1A" strokeWidth="1" />
      <line x1="30" y1="20" x2="38" y2="20" stroke="#1A1A1A" strokeWidth="1" />
      <line x1="44" y1="20" x2="52" y2="20" stroke="#1A1A1A" strokeWidth="1" />
    </svg>
  ),
  Balloon: ({ size = 48 }) => (
    <svg viewBox="0 0 48 48" width={size} height={size} fill="none" aria-hidden="true">
      <ellipse cx="24" cy="18" rx="12" ry="14" stroke="#1A1A1A" strokeWidth="1.5" />
      <path d="M22 32 L 24 34 L 26 32" stroke="#1A1A1A" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M24 34 C 24 38 28 40 26 44 C 25 46 23 44 24 42" stroke="#1A1A1A" strokeWidth="1.5" strokeLinecap="round" fill="none" />
    </svg>
  ),
  Star: ({ size = 40 }) => (
    <svg viewBox="0 0 40 40" width={size} height={size} fill="none" aria-hidden="true">
      <path d="M20 4 L 24 16 L 36 16.5 L 26.5 24 L 30 35.5 L 20 28.5 L 10 35.5 L 13.5 24 L 4 16.5 L 16 16 Z"
        stroke="#1A1A1A" strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  ),
  House: ({ size = 48 }) => (
    <svg viewBox="0 0 48 48" width={size} height={size} fill="none" aria-hidden="true">
      <path d="M8 22 L 24 8 L 40 22 L 40 42 L 8 42 Z" stroke="#1A1A1A" strokeWidth="1.5" strokeLinejoin="round" />
      <rect x="20" y="28" width="8" height="14" stroke="#1A1A1A" strokeWidth="1.5" />
      <line x1="12" y1="42" x2="4" y2="42" stroke="#1A1A1A" strokeWidth="1.5" strokeLinecap="round" />
      <line x1="36" y1="42" x2="44" y2="42" stroke="#1A1A1A" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  ),
  Cat: ({ size = 48 }) => (
    <svg viewBox="0 0 48 48" width={size} height={size} fill="none" aria-hidden="true">
      <path d="M14 20 L 10 10 L 18 16 M 34 20 L 38 10 L 30 16" stroke="#1A1A1A" strokeWidth="1.5" strokeLinejoin="round" />
      <ellipse cx="24" cy="26" rx="14" ry="12" stroke="#1A1A1A" strokeWidth="1.5" />
      <circle cx="19" cy="25" r="1.2" fill="#1A1A1A" />
      <circle cx="29" cy="25" r="1.2" fill="#1A1A1A" />
      <path d="M22 30 L 24 32 L 26 30" stroke="#1A1A1A" strokeWidth="1.5" strokeLinecap="round" />
      <line x1="20" y1="28" x2="14" y2="28" stroke="#1A1A1A" strokeWidth="1" />
      <line x1="28" y1="28" x2="34" y2="28" stroke="#1A1A1A" strokeWidth="1" />
    </svg>
  ),
  Menu: () => (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" aria-hidden="true">
      <line x1="4" y1="8" x2="20" y2="8" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
      <line x1="4" y1="16" x2="20" y2="16" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
    </svg>
  ),
  Close: ({ size = 20 }) => (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" aria-hidden="true">
      <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
      <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
    </svg>
  ),
  Lock: ({ size = 24 }) => (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" aria-hidden="true">
      <rect x="5" y="11" width="14" height="10" rx="2" stroke="#1A1A1A" strokeWidth="1.5" />
      <path d="M8 11 V 7 C 8 4.5 10 3 12 3 C 14 3 16 4.5 16 7 V 11" stroke="#1A1A1A" strokeWidth="1.5" />
    </svg>
  ),
  Shield: () => (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
      <path d="M12 3 L 4 6 V 12 C 4 17 12 21 12 21 C 12 21 20 17 20 12 V 6 Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M9 12 L 11 14 L 15 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  Infinity: () => (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
      <path d="M7 12 C 7 9.5 9 8 11 10.5 L 13 13.5 C 15 16 17 14.5 17 12 C 17 9.5 15 8 13 10.5 L 11 13.5 C 9 16 7 14.5 7 12 Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  ),
  Trash: () => (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
      <path d="M5 7 H 19 M 10 7 V 5 H 14 V 7 M 7 7 L 8 19 H 16 L 17 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  Plus: ({ size = 20 }) => (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" aria-hidden="true">
      <line x1="12" y1="5" x2="12" y2="19" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
      <line x1="5" y1="12" x2="19" y2="12" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
    </svg>
  ),
  Arrow: ({ size = 16 }) => (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" aria-hidden="true">
      <line x1="4" y1="12" x2="20" y2="12" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
      <path d="M14 6 L 20 12 L 14 18" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  ChevronDown: ({ size = 18 }) => (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" aria-hidden="true">
      <path d="M6 9 L 12 15 L 18 9" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  Check: ({ size = 16 }) => (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" aria-hidden="true">
      <path d="M5 12 L 10 17 L 19 7" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  Upload: ({ size = 36 }) => (
    <svg viewBox="0 0 48 48" width={size} height={size} fill="none" aria-hidden="true">
      <path d="M24 32 V 10 M 16 18 L 24 10 L 32 18" stroke="#1A1A1A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M8 30 V 38 C 8 39 9 40 10 40 H 38 C 39 40 40 39 40 38 V 30" stroke="#1A1A1A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
};

// --- Logo ---
function Logo({ onClick }) {
  return (
    <a href="#/" onClick={(e) => { e.preventDefault(); onClick && onClick(); navigate('/'); }} className="logo" aria-label="Crayo, acasă">
      <span>Crayo</span>
    </a>
  );
}

// --- Hybrid router (pathname for static SEO routes, hash for dynamic flows) ---
//
// Static routes (/, /despre, /contact, legals) are pre-rendered to real URLs
// at build time so Google + AI crawlers can index them. Dynamic routes
// (/create, /preview/<sid>, /checkout/<sid>, /download/<oid>) stay hash-based
// because they need the JS bundle and per-id pre-rendering would be
// pointless.
//
// Resolver: hash wins if present (so app-internal navigation keeps working
// the same way), otherwise we read pathname.
const STATIC_ROUTES = new Set([
  '/', '/despre', '/contact', '/termeni', '/confidentialitate', '/cookies',
]);

function getPath() {
  const hash = window.location.hash.replace(/^#/, '');
  if (hash) return hash;
  const pathname = window.location.pathname || '/';
  if (STATIC_ROUTES.has(pathname)) return pathname;
  return '/';
}
function navigate(path) {
  if (STATIC_ROUTES.has(path)) {
    // Use history API so the URL is shareable and the pre-rendered HTML
    // is what crawlers see on direct visits. Clear any stale hash so the
    // resolver reads pathname.
    if (window.location.hash) {
      window.history.replaceState(null, '', path);
    } else {
      window.history.pushState(null, '', path);
    }
    window.dispatchEvent(new Event('popstate'));
  } else {
    window.location.hash = path;
  }
  window.scrollTo({ top: 0, behavior: 'instant' });
}
// Used by Header + Footer for in-page anchor links ("Cum funcționează",
// "Exemple", "Preț"). Static routes like /contact use the History API
// rather than hash, so the previous `window.location.hash` check fell
// through to a synchronous scrollIntoView that found nothing on those
// pages. Now we read the resolved path and, when navigating away from
// a non-landing route, poll for the section up to ~1s while landing
// mounts.
function navigateToSection(id) {
  if (getPath() === '/') {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    return;
  }
  navigate('/');
  let attempts = 12;
  const tick = () => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    if (--attempts > 0) setTimeout(tick, 80);
  };
  setTimeout(tick, 80);
}
function useRoute() {
  const [path, setPath] = useState(getPath());
  useEffect(() => {
    const onChange = () => setPath(getPath());
    window.addEventListener('hashchange', onChange);
    window.addEventListener('popstate', onChange);
    return () => {
      window.removeEventListener('hashchange', onChange);
      window.removeEventListener('popstate', onChange);
    };
  }, []);
  return path;
}

// --- Header ---
function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  const go = (path) => (e) => {
    e.preventDefault();
    setMenuOpen(false);
    navigate(path);
  };
  const scrollTo = (id) => (e) => {
    e.preventDefault();
    setMenuOpen(false);
    navigateToSection(id);
  };
  return (
    <header className={'header' + (scrolled ? ' scrolled' : '')}>
      <div className="container header-inner">
        <Logo onClick={() => setMenuOpen(false)} />
        <nav>
          <ul className={'nav-links' + (menuOpen ? ' open' : '')}>
            <li><a href="#how" onClick={scrollTo('how')}>Cum funcționează</a></li>
            <li><a href="#examples" onClick={scrollTo('examples')}>Exemple</a></li>
            <li><a href="#pricing" onClick={scrollTo('pricing')}>Preț</a></li>
            <li><a href="#/contact" onClick={go('/contact')}>Contact</a></li>
            <li>
              <a href="#/create" onClick={go('/create')} className="btn btn-primary" style={{ padding: '10px 18px', fontSize: 14 }}>
                Încearcă gratuit
              </a>
            </li>
          </ul>
        </nav>
        <button className="hamburger" onClick={() => setMenuOpen(!menuOpen)} aria-label="Meniu">
          {menuOpen ? <Icon.Close /> : <Icon.Menu />}
        </button>
      </div>
    </header>
  );
}

// --- Footer ---
//
// WHY the copyright line says SHIPDRONES SRL: SHIPDRONES SRL is the
// registered operator (per SHIP.md + StaticPages.jsx legal text).
// "Crayo" is the product name, not a legal entity.
//
// NOTE on the EU ODR/SOL platform: the EU shut down ec.europa.eu/consumers/odr/
// in mid-2025, so we no longer link to it from the Legal column.
function Footer() {
  const go = (path) => (e) => { e.preventDefault(); navigate(path); };
  const goSection = (id) => (e) => {
    e.preventDefault();
    navigateToSection(id);
  };
  return (
    <footer className="footer">
      <div className="container">
        <div className="footer-grid">
          <div className="footer-col">
            <Logo />
            <p className="muted" style={{ fontSize: 14, marginTop: 12, maxWidth: 280 }}>
              Cartea ta de colorat, din pozele voastre.
            </p>
            <div style={{ marginTop: 20, display: 'flex', gap: 12, opacity: 0.6 }}>
              <Icon.Balloon size={36} />
              <Icon.Star size={28} />
              <Icon.Cat size={36} />
            </div>
          </div>
          <div className="footer-col">
            <h4>Produs</h4>
            <ul>
              <li><a href="#/create" onClick={go('/create')}>Încearcă</a></li>
              <li><a href="#examples" onClick={goSection('examples')}>Exemple</a></li>
              <li><a href="#pricing" onClick={goSection('pricing')}>Preț</a></li>
            </ul>
          </div>
          <div className="footer-col">
            <h4>Companie</h4>
            <ul>
              <li><a href="#/despre" onClick={go('/despre')}>Despre</a></li>
              <li><a href="#/contact" onClick={go('/contact')}>Contact</a></li>
            </ul>
          </div>
          <div className="footer-col">
            <h4>Legal</h4>
            <ul>
              <li><a href="#/termeni" onClick={go('/termeni')}>Termeni</a></li>
              <li><a href="#/confidentialitate" onClick={go('/confidentialitate')}>Confidențialitate</a></li>
              <li><a href="#/cookies" onClick={go('/cookies')}>Cookies</a></li>
            </ul>
          </div>
        </div>
        <div className="footer-bottom">
          <span>© 2026 SHIPDRONES SRL · CUI 46378099 · Reg. Com. J01/831/2022 · Făcut cu drag în România.</span>
          <span>salut@crayo.ro</span>
        </div>
      </div>
    </footer>
  );
}

// --- Toast system ---
const ToastContext = React.createContext({ push: () => {} });
function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const push = useCallback((msg) => {
    const id = Math.random().toString(36).slice(2);
    setToasts((t) => [...t, { id, msg }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 3400);
  }, []);
  return (
    <ToastContext.Provider value={{ push }}>
      {children}
      <div className="toast-stack" role="status" aria-live="polite">
        {toasts.map((t) => (<div key={t.id} className="toast">{t.msg}</div>))}
      </div>
    </ToastContext.Provider>
  );
}
function useToast() { return React.useContext(ToastContext); }

// --- Cookie banner ---
//
// Granular consent UI: Necessary (always on, not toggleable), Analytics
// (Google Analytics 4), Marketing (Meta Pixel + Conversions API).
// State lives in src/consent.js; this component just renders + wires the
// buttons. The banner re-shows automatically if consent is cleared via
// the "Schimbă consimțământul" button on /cookies (we listen for the
// crayo-consent-changed event to re-render).
function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [customizing, setCustomizing] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    let timer = null;
    const evaluate = () => {
      if (shouldShowBanner()) {
        timer = setTimeout(() => setVisible(true), 600);
      } else {
        setVisible(false);
      }
    };
    evaluate();
    // Re-evaluate when consent is cleared from /cookies → banner pops back.
    const unsub = subscribe(() => {
      if (timer) { clearTimeout(timer); timer = null; }
      evaluate();
    });
    return () => {
      if (timer) clearTimeout(timer);
      unsub();
    };
  }, []);

  if (!visible) return null;

  const acceptAll = () => {
    setConsent({ analytics: true, marketing: true });
    setVisible(false);
    // Pixel + GA4 init scripts read consent on page load, so a reload
    // gives the cleanest first-grant flow. Subsequent toggles via the
    // /cookies page can update Pixel/GA4 in place via Consent Mode.
    window.location.reload();
  };
  const onlyNecessary = () => {
    setConsent({ analytics: false, marketing: false });
    setVisible(false);
  };
  const savePrefs = () => {
    setConsent({ analytics, marketing });
    setVisible(false);
    // Reload only if anything was granted — pure "necessary" choice
    // doesn't load any new scripts.
    if (analytics || marketing) window.location.reload();
  };

  return (
    <div className="cookie-banner" role="dialog" aria-label="Consimțământ cookies">
      <p>
        Folosim cookies pentru funcționarea site-ului. Cele de analiză și marketing sunt opționale.{' '}
        <a href="#/cookies" className="link" onClick={(e) => { e.preventDefault(); navigate('/cookies'); }}>Detalii</a>
      </p>
      {customizing ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: 'var(--muted)' }}>
            <input type="checkbox" checked disabled aria-readonly="true" />
            <span><strong style={{ color: 'var(--ink, #1A1A1A)' }}>Necesare</strong> — sesiunea de creație, plata, monitorizarea erorilor (mereu active)</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
            <input
              type="checkbox"
              checked={analytics}
              onChange={(e) => setAnalytics(e.target.checked)}
            />
            <span><strong>Analiză</strong> — Google Analytics, înțelegem cum folosesc oamenii site-ul</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
            <input
              type="checkbox"
              checked={marketing}
              onChange={(e) => setMarketing(e.target.checked)}
            />
            <span><strong>Marketing</strong> — Meta Pixel, ne ajută să măsurăm reclamele de pe Facebook</span>
          </label>
          <div className="cookie-actions" style={{ marginTop: 8 }}>
            <button className="btn btn-ghost" onClick={() => setCustomizing(false)}>Înapoi</button>
            <button className="btn btn-primary" onClick={savePrefs}>Salvează preferințele</button>
          </div>
        </div>
      ) : (
        <div className="cookie-actions">
          <button className="btn btn-ghost" onClick={onlyNecessary}>Doar necesare</button>
          <button className="btn btn-ghost" onClick={() => setCustomizing(true)}>Personalizează</button>
          <button className="btn btn-primary" onClick={acceptAll}>Accept toate</button>
        </div>
      )}
    </div>
  );
}

// --- Page layout wrapper ---
function Page({ children, bg }) {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <>
      <Header />
      <main style={{ flex: 1, background: bg || 'var(--paper)' }}>{children}</main>
      <Footer />
    </>
  );
}

// --- Placeholder photo / line-art ---
// Pseudo-photo: subtle toned rectangle with caption
function PhotoPlaceholder({ label = 'Poză de familie', seed = 1, style = {} }) {
  // generate a soft "photo" feel using simple shapes in an SVG
  const hues = [
    { sky: '#D8C9A8', ground: '#B89D73', skin: '#E9BFA1' },
    { sky: '#C7B79A', ground: '#9A8568', skin: '#DDAF92' },
    { sky: '#D4C3A5', ground: '#A08A6B', skin: '#E4B89A' },
    { sky: '#CEC0A2', ground: '#8C7759', skin: '#D7AD8F' },
    { sky: '#DBCCAE', ground: '#AB9476', skin: '#ECC2A6' },
    { sky: '#C5B698', ground: '#927E60', skin: '#DBAF93' },
  ];
  const h = hues[seed % hues.length];
  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden', borderRadius: 'inherit', ...style }}>
      <svg viewBox="0 0 400 300" preserveAspectRatio="xMidYMid slice" width="100%" height="100%">
        <rect width="400" height="300" fill={h.sky} />
        <rect y="190" width="400" height="110" fill={h.ground} />
        <circle cx="330" cy="60" r="28" fill="#F4E4C4" opacity="0.7" />
        {/* Silhouettes */}
        <g>
          <ellipse cx="140" cy="160" rx="26" ry="30" fill={h.skin} />
          <path d="M110 210 Q 140 180 170 210 L 170 270 L 110 270 Z" fill={h.skin} />
          <ellipse cx="220" cy="170" rx="22" ry="24" fill={h.skin} />
          <path d="M196 220 Q 220 196 244 220 L 244 270 L 196 270 Z" fill={h.skin} />
          <ellipse cx="280" cy="185" rx="16" ry="18" fill={h.skin} />
          <path d="M262 222 Q 280 206 298 222 L 298 270 L 262 270 Z" fill={h.skin} />
        </g>
      </svg>
      <div style={{ position: 'absolute', bottom: 8, left: 10, fontFamily: 'ui-monospace, monospace', fontSize: 10, color: 'rgba(26,26,26,0.45)', letterSpacing: '0.08em' }}>
        [ {label} ]
      </div>
    </div>
  );
}

// Line-art version of the photo
function LineArtPlaceholder({ seed = 1, label = 'Pagină de colorat', style = {} }) {
  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', background: 'var(--paper)', borderRadius: 'inherit', overflow: 'hidden', ...style }}>
      <svg viewBox="0 0 400 300" preserveAspectRatio="xMidYMid slice" width="100%" height="100%" fill="none" stroke="#1A1A1A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        {/* ground line */}
        <path d="M 0 235 Q 100 228 200 235 T 400 232" />
        {/* sun */}
        <circle cx="330" cy="60" r="22" />
        <line x1="330" y1="28" x2="330" y2="18" />
        <line x1="358" y1="60" x2="368" y2="60" />
        <line x1="350" y1="40" x2="358" y2="32" />
        {/* little clouds */}
        <path d="M 40 58 q 10 -12 22 -4 q 10 -10 22 0 q 8 8 -4 14 q -10 10 -22 2 q -14 6 -18 -12 z" />
        {/* family silhouettes as simple outlined figures */}
        <g>
          {/* parent 1 */}
          <circle cx="140" cy="158" r="22" />
          <path d="M112 215 Q 140 178 168 215 L 168 270 L 112 270 Z" />
          <path d="M130 160 q 5 -4 10 0 M 138 170 q 3 2 6 0" />
          {/* parent 2 */}
          <circle cx="220" cy="168" r="18" />
          <path d="M200 220 Q 220 196 240 220 L 240 270 L 200 270 Z" />
          {/* kid */}
          <circle cx="280" cy="182" r="13" />
          <path d="M265 225 Q 280 208 295 225 L 295 270 L 265 270 Z" />
          <path d="M274 184 q 3 -2 6 0 M 278 192 q 2 1 4 0" />
        </g>
        {/* grass tufts */}
        <path d="M 30 248 l 4 -8 l 4 8 M 78 252 l 3 -6 l 3 6 M 360 246 l 4 -8 l 4 8" />
        {/* hand-drawn imperfect frame */}
        <rect x="6" y="6" width="388" height="288" rx="4" strokeDasharray="0 0" opacity="0.3" />
      </svg>
      <div style={{ position: 'absolute', bottom: 8, right: 10, fontFamily: 'ui-monospace, monospace', fontSize: 10, color: 'rgba(26,26,26,0.35)', letterSpacing: '0.08em' }}>
        [ {label} ]
      </div>
    </div>
  );
}

Object.assign(window, {
  useState, useEffect, useRef, useCallback,
  Icon, Logo, Header, Footer, Page,
  ToastContext, ToastProvider, useToast,
  CookieBanner,
  getPath, navigate, useRoute,
  PhotoPlaceholder, LineArtPlaceholder,
});
